import { useRef, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@/context/I18nContext';
import type { TrackLapHistogramBucket } from '@/types/telemetry';

interface TrackLapHistogramProps {
  histogram?: TrackLapHistogramBucket[];
}

const BUCKET_STEP_MS = 100;
const WINDOW_DURATION_MS = 30000;
const TOTAL_BUCKETS = WINDOW_DURATION_MS / BUCKET_STEP_MS + 1; // 301 buckets (0.0s .. +30.0s)

/** Format a 100ms-aligned timestamp as M:SS.s (matching leaderboard M:SS.mmm format at 0.1s precision). */
function formatBucketTime(ms: number): string {
  if (!ms || ms <= 0) return '—';
  const totalTenths = Math.round(ms / 100);
  const min = Math.floor(totalTenths / 600);
  const secTenths = totalTenths % 600;
  const sec = (secTenths / 10).toFixed(1).padStart(4, '0');
  return `${min}:${sec}`;
}

export function TrackLapHistogram({ histogram = [] }: TrackLapHistogramProps) {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const { minBucketMs, denseCounts, maxCount } = useMemo(() => {
    if (!histogram || histogram.length === 0) {
      return { minBucketMs: 0, denseCounts: [] as number[], maxCount: 0 };
    }
    let minMs = Infinity;
    const byBucket = new Map<number, number>();
    for (const b of histogram) {
      if (b.bucketMs > 0 && b.count > 0) {
        const aligned = Math.floor(b.bucketMs / BUCKET_STEP_MS) * BUCKET_STEP_MS;
        if (aligned < minMs) minMs = aligned;
        byBucket.set(aligned, (byBucket.get(aligned) || 0) + b.count);
      }
    }
    if (!isFinite(minMs)) {
      return { minBucketMs: 0, denseCounts: [] as number[], maxCount: 0 };
    }

    const counts = new Array<number>(TOTAL_BUCKETS).fill(0);
    let max = 0;
    for (let i = 0; i < TOTAL_BUCKETS; i++) {
      const bucketMs = minMs + i * BUCKET_STEP_MS;
      const c = byBucket.get(bucketMs) || 0;
      counts[i] = c;
      if (c > max) max = c;
    }

    return {
      minBucketMs: minMs,
      denseCounts: counts,
      maxCount: max,
    };
  }, [histogram]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || denseCounts.length === 0 || maxCount === 0) return;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      if (w <= 0 || h <= 0) return;

      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const ml = 42;
      const mr = 14;
      const mt = 12;
      const mb = 26;
      const pw = Math.max(1, w - ml - mr);
      const ph = Math.max(1, h - mt - mb);

      // Horizontal grid lines & Y-axis labels
      const ySteps = 3;
      ctx.font = '10px "JetBrains Mono", monospace';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';

      for (let s = 0; s <= ySteps; s++) {
        const frac = s / ySteps;
        const y = mt + ph - frac * ph;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(ml, y);
        ctx.lineTo(w - mr, y);
        ctx.stroke();

        const val = Math.round(frac * maxCount);
        if (s === 0 || val > 0) {
          ctx.fillStyle = '#5c5d6a';
          ctx.fillText(String(val), ml - 8, y);
        }
      }

      // X-axis tick labels (0.1s buckets, step every 2s or 5s depending on width)
      const tickStepSec = pw < 500 ? 5 : 2;
      const firstTickMs = Math.ceil(minBucketMs / (tickStepSec * 1000)) * (tickStepSec * 1000);
      const endBucketMs = minBucketMs + WINDOW_DURATION_MS;

      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#5c5d6a';

      const slotWidth = pw / TOTAL_BUCKETS;

      for (let tickMs = firstTickMs; tickMs <= endBucketMs; tickMs += tickStepSec * 1000) {
        const idx = Math.round((tickMs - minBucketMs) / BUCKET_STEP_MS);
        if (idx < 0 || idx >= TOTAL_BUCKETS) continue;
        const x = ml + (idx + 0.5) * slotWidth;

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
        ctx.beginPath();
        ctx.moveTo(x, mt + ph);
        ctx.lineTo(x, mt + ph + 4);
        ctx.stroke();

        ctx.fillText(formatBucketTime(tickMs), x, mt + ph + 6);
      }

      // Hover highlight column
      if (hoveredIdx !== null && hoveredIdx >= 0 && hoveredIdx < TOTAL_BUCKETS) {
        const hx = ml + hoveredIdx * slotWidth;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.04)';
        ctx.fillRect(hx, mt, Math.max(1, slotWidth), ph);
      }

      // Draw 0.1s histogram bars
      const gap = slotWidth > 3 ? 0.75 : 0;
      const barW = Math.max(1, slotWidth - gap);

      for (let i = 0; i < TOTAL_BUCKETS; i++) {
        const count = denseCounts[i];
        if (count <= 0) continue;

        const barH = Math.max(2, (count / maxCount) * ph);
        const x = ml + i * slotWidth + gap / 2;
        const y = mt + ph - barH;

        const isHovered = hoveredIdx === i;
        const isFastest = i === 0;

        if (isHovered) {
          ctx.fillStyle = '#9178ff';
        } else if (isFastest) {
          ctx.fillStyle = '#c084fc';
        } else {
          ctx.fillStyle = 'rgba(124, 92, 252, 0.78)';
        }

        ctx.beginPath();
        if (barW >= 3 && barH >= 3) {
          ctx.roundRect(x, y, barW, barH, [2, 2, 0, 0]);
        } else {
          ctx.rect(x, y, barW, barH);
        }
        ctx.fill();
      }
    };

    draw();

    const ro = new ResizeObserver(() => draw());
    ro.observe(container);
    return () => ro.disconnect();
  }, [denseCounts, maxCount, minBucketMs, hoveredIdx]);

  if (denseCounts.length === 0 || maxCount === 0) {
    return null;
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const ml = 42;
    const mr = 14;
    const pw = Math.max(1, rect.width - ml - mr);

    if (mx < ml || mx > rect.width - mr) {
      setHoveredIdx(null);
      setTooltipPos(null);
      return;
    }

    const idx = Math.min(
      TOTAL_BUCKETS - 1,
      Math.max(0, Math.floor(((mx - ml) / pw) * TOTAL_BUCKETS)),
    );
    setHoveredIdx(idx);
    setTooltipPos({ x: mx, y: my });
  };

  const handleMouseLeave = () => {
    setHoveredIdx(null);
    setTooltipPos(null);
  };

  const hoveredBucketMs = hoveredIdx !== null ? minBucketMs + hoveredIdx * BUCKET_STEP_MS : 0;
  const hoveredCount = hoveredIdx !== null ? denseCounts[hoveredIdx] || 0 : 0;

  return (
    <div className="card p-4 mb-4 w-full">
      <div
        ref={containerRef}
        className="relative w-full"
        style={{ height: 150 }}
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0 block w-full h-full"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
        {hoveredIdx !== null && tooltipPos && (
          <div
            className="absolute pointer-events-none z-10 px-2.5 py-1.5 rounded-md bg-bg-raised border border-border-default text-xs mono text-text-primary shadow-lg whitespace-nowrap"
            style={{
              left: Math.min(Math.max(tooltipPos.x, 65), (containerRef.current?.clientWidth || 300) - 65),
              top: Math.max(4, tooltipPos.y - 40),
              transform: 'translateX(-50%)',
            }}
          >
            {formatBucketTime(hoveredBucketMs)} – {formatBucketTime(hoveredBucketMs + BUCKET_STEP_MS)} · {t('sessionList.lapsUnit', { n: String(hoveredCount) })}
          </div>
        )}
      </div>
    </div>
  );
}
